import type { UrlBuilder } from "@/lib/navigation/createUrlBuilder";

/**
 * Map a Fans United entity (returned by the extract-entities API) to a relative
 * URL on the current tenant's site.
 *
 * Returns null when the tenant's site type has no route for this entity type,
 * e.g. COMPETITION on a `football-competition-site` tenant — the site IS the
 * competition, so there's no detail page to link to. The caller should fall
 * back to rendering plain text in that case.
 */
export function buildEntityUrl(
    entityId: string,
    entityType: string,
    urlBuilder: UrlBuilder,
): string | null {
    // Team/player/competition routes exist for football only. Other sports have no
    // entity pages; urlBuilder.sportEvent is the only multi-sport route.
    if (!entityId.startsWith("fb:")) return null;

    switch (entityType) {
        case "TEAM":
            return safeBuild(() => urlBuilder.team(entityId));
        case "ATHLETE":
            return safeBuild(() => urlBuilder.player(entityId));
        case "COMPETITION":
            return safeBuild(() => urlBuilder.footballCompetition(entityId));
        default:
            return null;
    }
}

function safeBuild(fn: () => string): string | null {
    try {
        return fn();
    } catch {
        // `createUrlBuilder` throws "No path template for page X in site type Y"
        // when the entity type has no route on the current site type.
        return null;
    }
}
